import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

/**
 * Mirrors the Cognito password policy on rap-almanac-users-dev / -stage / -prod,
 * read from the pools themselves:
 *
 *   MinimumLength: 8, RequireUppercase, RequireLowercase, RequireNumbers,
 *   RequireSymbols — all true. Cognito's own hard ceiling is 256 characters.
 *
 * The rule is MATCH the policy, never exceed it. A frontend stricter than the
 * backend rejects passwords Cognito would have accepted, and (because a disabled
 * button is not an explanation) the user gets a dead form with no reason. That is
 * exactly what the previous allow-list did: it omitted - _ + = [ ] ; ' / \ ~ `
 * so "Rap-Almanac1" and "Summer_2026A" were called weak.
 */
export const PASSWORD_POLICY = {
  minLength: 8,
  maxLength: 256,
} as const;

/**
 * Shown next to the field. Sourced from the same module as the rules so the copy
 * cannot drift away from what is actually enforced.
 */
export const PASSWORD_REQUIREMENTS_HINT =
  'At least 8 characters, including an uppercase letter, a lowercase letter, a number and a symbol.';

/** One flag per rule, so the UI can name precisely what is missing. */
export interface PasswordStrengthFailures {
  minLength: boolean;
  maxLength: boolean;
  uppercase: boolean;
  lowercase: boolean;
  number: boolean;
  symbol: boolean;
}

export function passwordValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    if (!value) {
      return null; // Emptiness is Validators.required's job, not this one's.
    }

    const text = String(value);

    const failures: PasswordStrengthFailures = {
      minLength: text.length < PASSWORD_POLICY.minLength,
      // Cognito rejects beyond 256; without this the frontend would be LOOSER
      // than the backend and the user would only find out from a server error.
      maxLength: text.length > PASSWORD_POLICY.maxLength,
      uppercase: !/[A-Z]/.test(text),
      lowercase: !/[a-z]/.test(text),
      number: !/[0-9]/.test(text),
      // Any non-alphanumeric counts. Cognito documents a specific symbol list,
      // but deliberately NOT reproducing it here: an allow-list is what caused
      // the original bug, and the documented list omits characters Cognito
      // accepts in practice (a hyphen among them). Erring loose is the safe
      // direction — a password Cognito dislikes returns a server message the
      // user can read, whereas erring strict produces a silent dead form.
      symbol: !/[^A-Za-z0-9]/.test(text),
    };

    const failed = Object.values(failures).some(Boolean);

    // Keeps the historical `passwordStrength` key so existing
    // *ngIf="errors?.['passwordStrength']" checks still fire, while carrying the
    // per-rule detail needed to say WHICH requirement was missed.
    return failed ? { passwordStrength: failures } : null;
  };
}
